import {
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
  type AlertColor,
  type AlertPropsColorOverrides,
} from '@mui/material';
import { Html5Qrcode } from 'html5-qrcode';
import { Fragment, useEffect, useRef, useState } from 'react';
import moment from 'moment';

import { getVoucher, redeemVoucher } from '../fetchFns';
import useSnackbar from '../hooks/useSnackbar';
import useBackdrop from '../hooks/useBackdrop';
import type { Voucher } from '../models/types';
import useDialog from '../hooks/useDialog';
import type { OverridableStringUnion } from '@mui/types';
import Bugsnag from '@bugsnag/js';

const qrRegionId = 'qr-reader-region';

// interface CodeData {
//   code: string;
//   campaignName: string;
//   expiryDate: Date;
//   refereeBenefitType: 'percentage' | 'amount' | 'item';
//   minimumSpend?: number;
//   refereeBenefitDescription: string;
// }

interface CodeListItem {
  name: string;
  text: string;
}

/**
 * ScanRefereeQr is a React component that renders a QR code scanner using the `html5-qrcode` library.
 * It allows users to scan a QR code (typically displayed on another device) and displays the scanned data upon success.
 * The component uses Material UI for layout and styling, and interacts with a DataContext to show notifications.
 *
 * If the video feed is duplicated, it's because StrictMode is on in development.
 *
 * Documentation: https://blog.minhazav.dev/research/html5-qrcode.html
 *
 * @component
 * @example
 * <ScanRefereeQr />
 */
export default function ScanRefereeQr({
  onRedeemed,
}: {
  /** Called after a voucher is successfully redeemed, so callers can refresh
   * data affected by the redemption (e.g. the dashboard balance). */
  onRedeemed?: () => void;
}) {
  const snackbar = useSnackbar();
  const dialog = useDialog();
  const backdrop = useBackdrop();
  const [remarks, setRemarks] = useState<string>('');
  const [qrScanState, setQrScanState] = useState<
    'loading' | 'voucherExists' | 'voucherDoesNotExist' | 'error'
  >('loading');
  const [voucherData, setVoucherData] = useState<Voucher>();
  const [codeListItems, setCodeListItems] = useState<CodeListItem[]>([]);
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const [loading, setLoading] = useState(true);

  const handleQrReaderInitError = (err: any) => {
    Bugsnag.notify(err);
    // Ignore QR code parse errors (i.e. no code found in frame)
    if (
      typeof err === 'string' &&
      err.includes('No MultiFormat Readers were able to detect the code.')
    )
      return;

    console.log(err);
    let message = `Unable to start QR scanner: ${err}`;

    if (
      err ===
      'Error getting userMedia, error = NotAllowedError: Permission denied'
    ) {
      message = 'Camera permission not granted, please allow camera access.';
    }

    snackbar.handleSnackbarOpen(message, 'error');
  };

  const handleRedeemVoucher = async (voucherCode: string) => {
    backdrop.handleBackdropOpen();

    let message = 'Voucher redeemed!';
    let severity:
      | OverridableStringUnion<AlertColor, AlertPropsColorOverrides>
      | undefined = 'success';

    try {
      await redeemVoucher(voucherCode, remarks);

      // Redemption changes the brand balance — let the caller refresh it.
      onRedeemed?.();
      dialog.handleDialogClose();
    } catch (error) {
      const errorCast = error as Error;
      console.error(errorCast);
      message = errorCast.message;
      severity = 'error';
    }

    snackbar.handleSnackbarOpen(message, severity);
    backdrop.handleBackdropClose();
  };

  const handleReadCode = async (decodedText: string) => {
    await html5QrCodeRef.current!.stop();
    setLoading(true);
    backdrop.handleBackdropOpen();
    try {
      // Some fetching logic to read DB for code data
      // const voucherData: Voucher = {
      //   campaign_name: 'Pizza Hut 1-for-1',
      //   voucher_code: 'gwj93',
      //   expiry_date: '2025-01-01',
      //   referee_benefit_type: 'item',
      //   referee_benefit_description: 'Get a free pizza when you buy one.',
      //   referee_benefit_amount: 2,
      //   minimum_spend: 3
      // };

      const voucherDataResponse = await getVoucher(decodedText);

      if (voucherDataResponse.error) {
        throw new Error('An error occurred while reading voucher.');
      }

      const voucherData: Voucher = voucherDataResponse.data.data;

      setQrScanState('voucherExists');
      setVoucherData(voucherData);

      setCodeListItems([
        {
          name: 'Code',
          text: voucherData.voucher_code,
        },
        {
          name: 'Campaign Name',
          text: voucherData.campaign_name,
        },
        {
          name: 'Expiry Date',
          text: moment(voucherData.expiry_date).format('DD MMM YYYY'),
        },
        {
          name: 'Minimum Spend',
          text: voucherData.minimum_spend
            ? voucherData.minimum_spend.toFixed(2)
            : '0',
        },
        {
          name: 'Benefit',
          text: voucherData.referee_benefit_description,
        },
      ]);
    } catch (error) {
      const errorCast = error as Error;
      console.error(errorCast);
      if (errorCast.message === 'Voucher does not exist.') {
        setQrScanState('voucherDoesNotExist');
      } else {
        setQrScanState('error');
      }
      snackbar.handleSnackbarOpen(errorCast.message, 'error');
    }

    backdrop.handleBackdropClose();
  };

  useEffect(() => {
    html5QrCodeRef.current = new Html5Qrcode(qrRegionId);
    html5QrCodeRef.current
      .start(
        { facingMode: 'environment' },
        {
          fps: 10,
          // qrbox: { width: 200, height: 200 },
        },
        handleReadCode,
        (error: any) => {
          // Ignore QR code parse errors (i.e. no code found in frame)
          if (
            typeof error === 'string' &&
            (error as string).includes(
              'No MultiFormat Readers were able to detect the code.',
            )
          )
            return;
          if (
            error ===
            'QR code parse error, error = No barcode or QR code detected.'
          )
            return;
          console.error('An error occurred with the HTML QR reader.');
          console.error(typeof error);
          console.error(error);
          snackbar.handleSnackbarOpen(error, 'error');
        },
      )
      .then(() => {
        setLoading(false);
      })
      .catch(handleQrReaderInitError);

    return () => {
      console.log('Unmounting, stopping QR');

      const qrScannerState = html5QrCodeRef.current?.getState();

      // If the scanner is not in NOT_STARTED (1) state, stop it.
      if (qrScannerState !== 1) {
        html5QrCodeRef.current?.stop().catch((err) => {
          console.error('Unable to stop qr code');
          console.error(err);
        });
      }
    };
  }, []);

  const scanVoucherAgainButton = (
    <Button
      onClick={async () => {
        setQrScanState('loading');
        await html5QrCodeRef.current!.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            // qrbox: { width: 200, height: 200 },
          },
          handleReadCode,
          (error: any) => {
            // Ignore QR code parse errors (i.e. no code found in frame)
            if (
              typeof error === 'string' &&
              (error as string).includes(
                'No MultiFormat Readers were able to detect the code.',
              )
            )
              return;
            if (
              error ===
              'QR code parse error, error = No barcode or QR code detected.'
            )
              return;
            console.error('An error occurred with the HTML QR reader.');
            console.error(typeof error);
            console.error(error);
            // dataCtx.handleSnackbarOpen(error, 'error');
          },
        );
        setLoading(false);
      }}>
      Scan Voucher Again
    </Button>
  );

  return (
    <Stack direction="column" alignItems="center" spacing={2}>
      {(() => {
        switch (qrScanState) {
          case 'loading':
            return (
              <>
                <Typography>
                  Please scan the customer's voucher QR code.
                </Typography>
                {loading && <CircularProgress />}
              </>
            );
          case 'voucherDoesNotExist':
            return (
              <Stack direction="column" spacing={2}>
                <Typography>This voucher does not exist.</Typography>
                {scanVoucherAgainButton}
              </Stack>
            );
          case 'error':
            return (
              <Stack direction="column" spacing={2}>
                <Typography>An error occurred. Please try again.</Typography>
                {scanVoucherAgainButton}
              </Stack>
            );
          case 'voucherExists':
            return (
              <>
                <List dense>
                  {codeListItems.map((val, i) => (
                    <Fragment key={val.name}>
                      {i !== 0 && <Divider />}
                      <ListItem
                        key={val.name}
                        disablePadding
                        sx={{ display: 'block' }}>
                        <ListItemText primary={val.name} secondary={val.text} />
                      </ListItem>
                    </Fragment>
                  ))}
                </List>
                <TextField
                  fullWidth
                  multiline
                  maxRows={2}
                  label="Remarks"
                  value={remarks}
                  onChange={(ev) => {
                    setRemarks(ev.target.value);
                  }}
                />
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="flex-end"
                  alignItems="center">
                  <Button
                    onClick={() => {
                      handleRedeemVoucher(voucherData!.voucher_code);
                    }}
                    // color="info"
                  >
                    Redeem Voucher
                  </Button>
                  <Button
                    onClick={() => {
                      dialog.handleDialogClose();
                    }}
                    // color="info"
                  >
                    Cancel
                  </Button>
                </Stack>
              </>
            );
          default:
            return null;
        }
      })()}
      <Box
        id={qrRegionId}
        style={{
          width: '100%',
          height: '100%',
          marginTop: 16,
          visibility: loading ? 'hidden' : 'visible',
        }}
      />
    </Stack>
  );
}
